import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Box, Typography } from '@mui/material';
import { Card } from '../common';
import { useProjectMenu } from '../../contexts/ProjectMenuContext';
import { AuthContext } from '../../contexts/AuthContext';
import PermissionGate from '../common/PermissionGate';

/**
 * ProjectCard Component
 * Displays a project card with metadata and actions
 * 
 * @param {Object} props - Component props
 * @param {Object} props.project - Project data
 * @param {Function} props.onStatusChange - Status change handler
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 * @param {Function} props.onClone - Clone handler
 * @param {boolean} props.loading - Loading state
 * @returns {React.ReactElement} Project card component
 */
const ProjectCard = ({
  project,
  onStatusChange,
  onEdit,
  onDelete,
  onClone,
  loading = false
}) => {
  const { menuSettings } = useProjectMenu();
  const { user, hasPermission } = useContext(AuthContext);

  // Check if current user can clone this project (admin or project owner)
  const canCloneProject = () => {
    if (!user) return false;
    const isAdmin = user.system_role === 'admin' || user.systemRole === 'admin';
    const projectOwnerId = project.owner?._id || project.owner?.id || project.owner;
    const isOwner = projectOwnerId && (String(projectOwnerId) === String(user._id) || String(projectOwnerId) === String(user.id));
    return isAdmin || isOwner;
  };
  // Function to get project code or initials
  function getProjectInitials(name) {
    if (!name) return '?';
    
    // First try to extract what looks like a code (2-3 uppercase letters followed by optional numbers)
    const codeMatch = name.match(/\b([A-Z]{1,3}\d*)\b/);
    if (codeMatch) {
      return codeMatch[0];
    }
    
    // Otherwise extract first letter of each word, up to 2 letters
    const initials = name
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
      
    return initials || name.charAt(0).toUpperCase();
  }
  
  // Function to generate a consistent color based on the project name
  function getAvatarColor(name) {
    if (!name) return '#6200EA'; // Default purple
    
    // List of colors to choose from
    const colors = [
      '#1E88E5', // Blue
      '#43A047', // Green
      '#E53935', // Red
      '#FB8C00', // Orange
      '#6D4C41', // Brown
      '#8E24AA', // Purple
      '#00ACC1', // Cyan
      '#3949AB', // Indigo
      '#039BE5', // Light Blue
      '#7CB342', // Light Green
      '#C0CA33', // Lime
      '#FDD835', // Yellow
      '#FFB300', // Amber
      '#F4511E', // Deep Orange
      '#5D4037', // Brown
      '#455A64', // Blue Grey
      '#EC407A', // Pink
      '#26A69A', // Teal
    ];
    
    // Generate a hash from the name to pick a consistent color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Use the hash to pick a color
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  
  // Handle menu open
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setAnchorEl(null);
  };
  
  // Handle status change - improved version with logging
  const handleStatusChange = async (newStatus, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    handleMenuClose();
    console.log('Status change project:', project, 'New status:', newStatus);
    
    if (onStatusChange) {
      try {
        onStatusChange(project, newStatus);
      } catch (err) {
        console.error('Error changing project status:', err);
      }
    } else {
      console.error('Status change handler not provided');
    }
  };
  
  // Handle delete
  const handleDelete = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    handleMenuClose();
    if (onDelete) {
      onDelete(project);
    }
  };
  
  // Handle edit - improved version with logging
  const handleEdit = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    handleMenuClose();
    console.log('Edit project:', project);
    
    if (onEdit) {
      try {
        onEdit(project);
      } catch (err) {
        console.error('Error editing project:', err);
      }
    } else {
      console.error('Edit handler not provided');
    }
  };
  
  // Handle clone - improved version with logging
  const handleClone = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    handleMenuClose();
    console.log('Clone project:', project);
    
    if (onClone) {
      try {
        onClone(project);
      } catch (err) {
        console.error('Error cloning project:', err);
      }
    } else {
      console.error('Clone handler not provided');
    }
  };
  
  // Get project ID safely
  const getProjectId = () => {
    return project._id || project.id;
  };
  
  // Handle card click
  const handleCardClick = () => {
    const projectId = getProjectId();
    console.log('Navigating to project:', projectId);
    navigate(`/projects/${projectId}`);
  };
  
  // Get status color
  const getStatusColor = () => {
    switch (project.status) {
      case 'completed':
        return 'success';
      case 'archived':
        return 'error';
      case 'in_progress':
        return 'warning';
      default:
        return 'info';
    }
  };
  
  // Get formatted status label
  const getStatusLabel = () => {
    switch (project.status) {
      case 'completed':
        return 'Completed';
      case 'archived':
        return 'Archived';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Active';
    }
  };
  
  // Format created date
  const formattedDate = project.createdAt
    ? formatDistanceToNow(new Date(project.createdAt), { addSuffix: true })
    : 'Unknown date';
  
  return (
    <Card
      elevated={false}
      status={project.status === 'archived' ? 'error' : null}
      headerAction={null}
      sx={{
        cursor: 'pointer',
        height: '100%', // Fill grid item
        maxWidth: '100%', // Allow responsive width
        minWidth: 100, // Smaller minimum width
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'left',
        position: 'relative',
        p: 0,
        borderRadius: '4px', // Reduced border-radius
        backgroundColor: '#e8f5e9', // Light green background
        margin: '0 !important',
        width: '100%',
        boxShadow: 'none', // No shadow
        transition: 'all 0.2s',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
      onClick={handleCardClick}
    >
      {/* Menu Icon - bottom right - only show if user has dashboard permissions and at least one menu option is enabled */}
      {(hasPermission('project.dashboard.edit') || hasPermission('project.dashboard.archive')) &&
        ((hasPermission('project.dashboard.edit') && (menuSettings.showEditOption || (menuSettings.showCloneOption && canCloneProject() && project.status !== 'archived'))) ||
         (hasPermission('project.dashboard.archive') && menuSettings.showArchiveOption)) && (
        <IconButton 
          aria-label="project actions" 
          onClick={handleMenuOpen}
          size="small"
          disabled={loading}
          sx={{ 
            position: 'absolute', 
            bottom: 4, 
            right: 2,
            opacity: 0.7,
            color: '#888',
            padding: '2px',
            '&:hover': {
              opacity: 1,
              backgroundColor: 'transparent'
            }
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
      
      {/* Card Content */}
      <Box sx={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        px: 1,
        py: 0.5
      }}>
        {/* Project short code */}
        <Typography 
          variant="h6"
          sx={{ 
            textAlign: "center",
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: 1.2,
            mb: 1,
            color: "#485869" // Using top navigation color for text instead
          }}
        >
          {project.shortCode || getProjectInitials(project.name)}
        </Typography>
        
        {/* Project name */}
        <Typography 
          variant="body1"
          sx={{ 
            textAlign: "center",
            fontWeight: 500,
            fontSize: "0.8rem",
            lineHeight: 1.2,
            color: "#000"
          }}
        >
          {project.name}
        </Typography>
      </Box>
      
      {/* Hidden elements - keeping them in the DOM but not displaying */}
      <Box sx={{ display: 'none' }}>
        <Typography variant="body2">
          {project.description || 'No description provided'}
        </Typography>
        
        <Box>
          <Tooltip title="Documents">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <DescriptionIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {project.documentCount || 0}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        
        <Box>
          <Typography variant="caption" color="text.secondary">
            Created {formattedDate}
          </Typography>
          
          <Chip 
            label={getStatusLabel()} 
            size="small" 
            color={getStatusColor()}
            variant={project.status === 'active' ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>
      
      {/* Project Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        elevation={3}
        sx={{ 
          '& .MuiPaper-root': { 
            minWidth: '180px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
          }
        }}
      >
        <PermissionGate permission="project.dashboard.edit">
          {menuSettings.showEditOption && (
            <MenuItem onClick={(e) => handleEdit(e)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}

          {menuSettings.showCloneOption && canCloneProject() && project.status !== 'archived' && (
            <MenuItem onClick={(e) => handleClone(e)}>
              <ListItemIcon>
                <FileCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Clone</ListItemText>
            </MenuItem>
          )}
        </PermissionGate>

        <PermissionGate permission="project.dashboard.archive">
          {menuSettings.showArchiveOption && (
            project.status !== 'archived' ? (
              <MenuItem onClick={(e) => handleStatusChange('archived', e)}>
                <ListItemIcon>
                  <ArchiveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Archive project</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={(e) => handleStatusChange('active', e)}>
                <ListItemIcon>
                  <UnarchiveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Unarchive Project</ListItemText>
            </MenuItem>
          )
        )}
        </PermissionGate>
      </Menu>
    </Card>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.object.isRequired,
  onStatusChange: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onClone: PropTypes.func,
  loading: PropTypes.bool
};

export default ProjectCard;